import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL",
  "IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE",
  "NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD",
  "TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const MATTER_CATEGORIES = [
  { key: "tenant", label: "Tenant / Landlord" },
  { key: "employment", label: "Employment / Wages" },
  { key: "family", label: "Family / Divorce / Custody" },
  { key: "small-claims", label: "Small Claims / Consumer" },
  { key: "immigration", label: "Immigration" },
  { key: "consumer", label: "Consumer / Debt" },
  { key: "estate", label: "Estate / Probate" },
  { key: "criminal", label: "Criminal (info only until sandbox opens)" },
  { key: "other", label: "Other" },
];

/**
 * LegalAdvicePage · /legal-advice
 *
 * Founder ruling 2026-08-21: walk all four regulatory doors in parallel to
 * render the lawyer profession obsolete. The point isn't captured revenue
 * (though it is); the point is that every American with a legal problem
 * should get real, licensed, plain-english advice for a wholesale price.
 */
export default function LegalAdvicePage() {
  const [policy, setPolicy] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    state_code: "",
    matter_category: "other",
    urgency: "background",
    situation: "",
    contact_note: "",
    switcher: false,
    current_provider: "",
    current_annual_cost_usd: "",
    switcher_proof_1_type: "",
    switcher_proof_1_reference: "",
    switcher_proof_2_type: "",
    switcher_proof_2_reference: "",
    hardship: false,
    hardship_categories: [],
    hardship_country: "",
    hardship_context: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/legal-advice/policy`).then(r => r.json()).then(setPolicy).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (form.name.trim().length < 2) { toast.error("Your name is required."); return; }
    if (!form.email.includes("@")) { toast.error("A real email is required."); return; }
    if (!form.state_code) { toast.error("Pick your state."); return; }
    if (form.situation.trim().length < 40) { toast.error("Describe the situation in at least a full sentence (40+ characters)."); return; }
    if (form.switcher) {
      if (form.current_provider.trim().length < 2) {
        toast.error("Switcher tier: name the provider you're currently paying.");
        return;
      }
      const p1 = form.switcher_proof_1_type && form.switcher_proof_1_reference.trim();
      const p2 = form.switcher_proof_2_type && form.switcher_proof_2_reference.trim();
      if (!p1 || !p2) {
        toast.error("Switcher tier requires TWO pieces of traceable proof · both a type and a reference for each.");
        return;
      }
    }
    if (form.hardship && form.hardship_categories.length === 0) {
      toast.error("Hardship tier: pick at least one qualifying category.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        current_annual_cost_usd: form.current_annual_cost_usd ? parseFloat(form.current_annual_cost_usd) : null,
      };
      const r = await fetch(`${API}/api/legal-advice/inquire`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok) {
        setSubmitted(d);
        if (form.hardship) toast.success("Received · hardship FREE FOREVER tier · pending brief eligibility review.");
        else if (form.switcher) toast.success("Received · switcher $99 tier · pending two-proof verification.");
        else toast.success("Received · queued to the door that fits your state.");
      } else {
        toast.error(d?.detail || "Something went wrong. Try again in a moment.");
      }
    } catch {
      toast.error("Network hiccup. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const toggleHardshipCategory = (key) => {
    setForm((f) => {
      const set = new Set(f.hardship_categories);
      set.has(key) ? set.delete(key) : set.add(key);
      return { ...f, hardship_categories: Array.from(set) };
    });
  };

  const doors = [
    { k: "door_1_sandbox", policyKey: "door_1_sandbox", tone: "accent" },
    { k: "door_2_attorney_on_retainer", policyKey: "door_2_attorney_on_retainer", tone: "text" },
    { k: "door_3_bar_endorsed", policyKey: "door_3_bar_endorsed", tone: "text" },
    { k: "door_4_foreign_flag", policyKey: "door_4_foreign_flag", tone: "text" },
  ];

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain" data-testid="legal-advice-page">
      <TopNav />
      <div className="mx-auto max-w-5xl px-4 md:px-8 py-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-3">
          THE LAWYER · OBSOLETE
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase mb-4" data-testid="legal-advice-headline">
          Sage Legal Advice
        </h1>
        <p className="text-lg text-sage-muted leading-relaxed max-w-3xl mb-8 sage-prose">
          Real legal advice — not just information — at wholesale price. Four regulatory doors are being walked in parallel so that every American with a legal problem can get real, licensed, plain-english help for a wholesale fee.
        </p>

        {policy && (
          <div className="border-2 border-sage-accent bg-sage-pale/30 p-6 mb-10" data-testid="legal-advice-line-card">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">The line every state draws</div>
            <p className="text-base text-sage-text sage-prose mb-3">{policy.the_line_every_state_draws}</p>
            <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">The house's answer</div>
            <p className="text-base text-sage-text sage-prose">{policy.the_house_ruling_2026_08_21}</p>
          </div>
        )}

        {/* PRICING · Founder ruling 2026-08-22 · $299 standard, $99 switcher, FREE hardship */}
        {policy?.pricing && (
          <div className="mb-10" data-testid="legal-advice-pricing">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-3">Pricing · flat annual · unlimited matters</div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="border-2 border-sage-accent bg-sage-pale/30 p-6" data-testid="pricing-standard">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-2">Standard tier</div>
                <div className="flex items-baseline gap-2 mb-2">
                  <div className="text-5xl font-bold tracking-tighter text-sage-text">${policy.pricing.standard_usd_per_year}</div>
                  <div className="text-sm text-sage-muted">/ person / year</div>
                </div>
                <p className="text-sm text-sage-muted sage-prose">
                  {policy.pricing.billing_shape}
                </p>
              </div>
              <div className="border-2 border-sage-text bg-sage-text text-sage-bg p-6" data-testid="pricing-switcher">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-bg opacity-70 mb-2">Switcher tier</div>
                <div className="flex items-baseline gap-2 mb-2">
                  <div className="text-5xl font-bold tracking-tighter">${policy.pricing.switcher_usd_per_year}</div>
                  <div className="text-sm opacity-80">/ person / year</div>
                  <div className="text-xs opacity-60 ml-2 line-through">${policy.pricing.standard_usd_per_year}</div>
                </div>
                <p className="text-sm sage-prose opacity-90">
                  {policy.pricing.switcher_qualifier}
                </p>
              </div>
              <div className="border-2 border-sage-accent2 bg-sage-accent2 text-sage-bg p-6" data-testid="pricing-hardship">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-bg opacity-70 mb-2">Hardship tier</div>
                <div className="flex items-baseline gap-2 mb-2">
                  <div className="text-4xl font-bold tracking-tighter">FREE</div>
                  <div className="text-sm opacity-80">/ forever</div>
                </div>
                <p className="text-sm sage-prose opacity-90">
                  {policy.pricing.hardship_qualifier}
                </p>
              </div>
            </div>
            <p className="text-xs text-sage-faint italic mt-3">
              Why this cheap: {policy.pricing.why_undercut}
            </p>
          </div>
        )}

        {/* COFFIN NAILS · Founder ruling 2026-08-22 · place on this page */}
        {policy?.coffin_nails_on_this_page && (
          <div className="mb-10 border-2 border-sage-text bg-sage-bg p-6" data-testid="legal-advice-nails">
            <div className="text-xs uppercase tracking-[0.25em] text-sage-text mb-4">Coffin nails · this door</div>
            <div className="space-y-6">
              {policy.coffin_nails_on_this_page.map((n) => (
                <div key={n.number} className="border-l-4 border-sage-accent pl-4" data-testid={`nail-${n.number}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">
                    Nail #{n.number} · {n.author}
                  </div>
                  <p className="text-2xl md:text-3xl text-sage-text sage-prose leading-snug italic mb-2">
                    "{n.kings_line}"
                  </p>
                  <p className="text-xs text-sage-faint italic">{n.signature}</p>
                  <p className="text-xs text-sage-muted italic mt-1">{n.forge_note}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Retained-lawyer endgame · Founder ruling 2026-08-22 */}
        {policy?.retained_lawyer_endgame && (
          <div className="mb-10 border border-sage-accent bg-sage-pale/20 p-5" data-testid="legal-advice-endgame-retained">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">The endgame · retained-lawyer free tier</div>
            <p className="text-sm text-sage-text sage-prose">{policy.retained_lawyer_endgame}</p>
          </div>
        )}

        <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-sage-text mb-6">
          The four doors
        </h2>
        {policy && (
          <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="legal-advice-doors">
            {doors.map((d, i) => {
              const p = policy[d.policyKey];
              if (!p) return null;
              return (
                <div
                  key={d.k}
                  data-testid={`legal-door-${i + 1}`}
                  className={`border ${d.tone === "accent" ? "border-2 border-sage-accent bg-sage-pale/30" : "border-sage-border"} p-5`}
                >
                  <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">
                    Door {i + 1}
                  </div>
                  <div className="text-xl font-bold uppercase tracking-tight text-sage-text mb-2">{p.name}</div>
                  <p className="text-sm text-sage-muted sage-prose mb-3">{p.shape}</p>
                  {p.jurisdictions && (
                    <div className="text-xs text-sage-text mb-1">
                      <span className="uppercase tracking-[0.15em] text-sage-faint">Jurisdictions · </span>
                      {p.jurisdictions.join(", ")}
                    </div>
                  )}
                  {p.jurisdictions_under_review && (
                    <div className="text-xs text-sage-text mb-1">
                      <span className="uppercase tracking-[0.15em] text-sage-faint">Under review · </span>
                      {p.jurisdictions_under_review.join(" · ")}
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent mt-2">
                    ETA · {p.eta}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {policy && (
          <div className="border border-sage-border bg-sage-pale/10 p-5 mb-10" data-testid="legal-advice-endgame">
            <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Why all four</div>
            <p className="text-sm text-sage-text sage-prose mb-3">{policy.why_all_four}</p>
            <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">The endgame</div>
            <p className="text-sm text-sage-text sage-prose">{policy.the_endgame}</p>
          </div>
        )}

        <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-sage-text mb-3" id="legal-advice-intake">
          File a matter · queue by state
        </h2>
        <p className="text-sm text-sage-muted sage-prose mb-6">
          Your matter routes to the door that legally serves your state today. In Utah and Arizona the sandbox opens next week. Every other state routes to the attorney-on-retainer queue as those hires land. Information tier is available immediately for every state — no wait.
        </p>

        {submitted ? (
          <div className="border-2 border-sage-accent bg-sage-pale/30 p-8" data-testid="legal-advice-submitted">
            <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-3">Filed</div>
            <div className="text-xl font-bold uppercase tracking-tight text-sage-text mb-3">
              Received. Queued to {submitted.assigned_door === "door_1_sandbox" ? "Door 1 · Sandbox" : "Door 2 · Attorney-on-Retainer"}.
            </div>
            <p className="text-sm text-sage-muted sage-prose mb-3">{submitted.door_note}</p>
            {submitted.quoted_price_usd !== undefined && submitted.quoted_price_usd !== null && (
              <div className="border border-sage-accent bg-sage-bg p-4 mb-3" data-testid="legal-advice-submitted-price">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">
                  Your price tier
                </div>
                <div className="text-2xl font-bold text-sage-text">
                  {submitted.price_tier === "hardship_free_forever" ? "FREE" : `$${submitted.quoted_price_usd}`}
                  <span className="text-sm text-sage-muted font-normal ml-2">
                    {submitted.price_tier === "hardship_free_forever"
                      ? "/ forever · hardship (pending brief eligibility review)"
                      : submitted.price_tier === "switcher_99"
                        ? "/ person / year · switcher (pending two-proof verification)"
                        : "/ person / year · standard"}
                  </span>
                </div>
              </div>
            )}
            <div className="text-xs text-sage-muted sage-prose">
              Reference: <span className="font-mono text-sage-text">{submitted.inquiry_id}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Your name</div>
                <input
                  data-testid="legal-advice-name"
                  type="text" value={form.name} onChange={update("name")} maxLength={200}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Email</div>
                <input
                  data-testid="legal-advice-email"
                  type="email" value={form.email} onChange={update("email")} maxLength={200}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Your state</div>
                <select
                  data-testid="legal-advice-state"
                  value={form.state_code} onChange={update("state_code")}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                >
                  <option value="">Pick your state…</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Matter type</div>
                <select
                  data-testid="legal-advice-category"
                  value={form.matter_category} onChange={update("matter_category")}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                >
                  {MATTER_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </label>
              <label className="block md:col-span-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Urgency</div>
                <select
                  data-testid="legal-advice-urgency"
                  value={form.urgency} onChange={update("urgency")}
                  className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                >
                  <option value="same-day">Same-day</option>
                  <option value="this-week">This week</option>
                  <option value="this-month">This month</option>
                  <option value="background">Background · no rush</option>
                </select>
              </label>
            </div>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">What happened · plain english</div>
              <textarea
                data-testid="legal-advice-situation"
                value={form.situation} onChange={update("situation")}
                maxLength={12000} rows={6}
                placeholder="One paragraph. Who did what to whom, when, and what outcome you want."
                className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
              />
              <div className="text-[10px] text-sage-faint mt-1">
                {form.situation.trim().length}/40 characters minimum
              </div>
            </label>

            {/* Switcher block · Founder ruling 2026-08-22 · TWO real proofs required */}
            <div className="border-2 border-sage-text bg-sage-pale/20 p-4" data-testid="switcher-block">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="switcher-checkbox"
                  checked={form.switcher}
                  onChange={(e) => setForm((f) => ({ ...f, switcher: e.target.checked, hardship: e.target.checked ? false : f.hardship }))}
                  className="mt-1 w-5 h-5 accent-sage-accent cursor-pointer"
                />
                <div>
                  <div className="text-sm font-bold uppercase tracking-tight text-sage-text">
                    I'm a switcher · drop me to $99/year
                  </div>
                  <div className="text-xs text-sage-muted sage-prose mt-1">
                    Currently paying another provider? Show us <strong>TWO pieces of traceable proof</strong> (e.g. a bill AND a bank statement · a retainer letter AND an invoice number · a receipt AND a welcome email). Founder ruling 2026-08-22: minimum two, real, verifiable.
                  </div>
                </div>
              </label>
              {form.switcher && (
                <div className="grid md:grid-cols-2 gap-3 mt-4" data-testid="switcher-fields">
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Current provider name</div>
                    <input
                      data-testid="switcher-provider"
                      type="text" value={form.current_provider} onChange={update("current_provider")}
                      maxLength={200}
                      placeholder="e.g. LegalZoom, Rocket Lawyer, my attorney"
                      className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                    />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">What you're paying them per year (USD)</div>
                    <input
                      data-testid="switcher-cost"
                      type="number" min="0" step="1"
                      value={form.current_annual_cost_usd} onChange={update("current_annual_cost_usd")}
                      placeholder="e.g. 480"
                      className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                    />
                  </label>
                  {/* Proof 1 */}
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">Proof #1 · type</div>
                    <select
                      data-testid="switcher-proof-1-type"
                      value={form.switcher_proof_1_type} onChange={update("switcher_proof_1_type")}
                      className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                    >
                      <option value="">Pick a type…</option>
                      <option value="bill">Bill</option>
                      <option value="receipt">Receipt</option>
                      <option value="invoice">Invoice</option>
                      <option value="retainer_letter">Retainer letter</option>
                      <option value="welcome_email">Welcome email</option>
                      <option value="bank_statement">Bank statement</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">Proof #1 · traceable reference</div>
                    <input
                      data-testid="switcher-proof-1-reference"
                      type="text" value={form.switcher_proof_1_reference} onChange={update("switcher_proof_1_reference")}
                      maxLength={400}
                      placeholder="e.g. Invoice #4321, Chase Aug 2026 line 14"
                      className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                    />
                  </label>
                  {/* Proof 2 */}
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">Proof #2 · type</div>
                    <select
                      data-testid="switcher-proof-2-type"
                      value={form.switcher_proof_2_type} onChange={update("switcher_proof_2_type")}
                      className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                    >
                      <option value="">Pick a type…</option>
                      <option value="bill">Bill</option>
                      <option value="receipt">Receipt</option>
                      <option value="invoice">Invoice</option>
                      <option value="retainer_letter">Retainer letter</option>
                      <option value="welcome_email">Welcome email</option>
                      <option value="bank_statement">Bank statement</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-1">Proof #2 · traceable reference</div>
                    <input
                      data-testid="switcher-proof-2-reference"
                      type="text" value={form.switcher_proof_2_reference} onChange={update("switcher_proof_2_reference")}
                      maxLength={400}
                      placeholder="e.g. Welcome email from support@… on 2026-06-01"
                      className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Hardship block · Founder ruling 2026-08-22 · FREE FOREVER */}
            <div className="border-2 border-sage-accent2 bg-sage-pale/10 p-4" data-testid="hardship-block">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="hardship-checkbox"
                  checked={form.hardship}
                  onChange={(e) => setForm((f) => ({ ...f, hardship: e.target.checked, switcher: e.target.checked ? false : f.switcher }))}
                  className="mt-1 w-5 h-5 accent-sage-accent2 cursor-pointer"
                />
                <div>
                  <div className="text-sm font-bold uppercase tracking-tight text-sage-text">
                    I qualify for hardship · <span className="text-sage-accent2">100% FREE FOREVER</span>
                  </div>
                  <div className="text-xs text-sage-muted sage-prose mt-1">
                    Every Sage legal product, service, and document — free forever — if you are: homeless · disabled · over 65 · fleeing violence · in eviction · or the bottom 10% of earners in your country. Brief eligibility review only. Founder ruling 2026-08-22.
                  </div>
                </div>
              </label>
              {form.hardship && (
                <div className="mt-4 space-y-3" data-testid="hardship-fields">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent2 mb-2">Qualifying category (check all that apply)</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        {k: "homeless", l: "Homeless"},
                        {k: "disabled", l: "Disabled"},
                        {k: "over_65", l: "Over 65"},
                        {k: "fleeing_violence", l: "Fleeing violence"},
                        {k: "in_eviction", l: "In eviction"},
                        {k: "bottom_10_pct_earners", l: "Bottom 10% earners"},
                      ].map((c) => (
                        <label key={c.k} className="flex items-center gap-2 cursor-pointer border border-sage-border p-2 hover:border-sage-accent2">
                          <input
                            type="checkbox"
                            data-testid={`hardship-cat-${c.k}`}
                            checked={form.hardship_categories.includes(c.k)}
                            onChange={() => toggleHardshipCategory(c.k)}
                            className="w-4 h-4 accent-sage-accent2 cursor-pointer"
                          />
                          <span className="text-xs text-sage-text">{c.l}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <label className="block">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Country (for bottom-10% earners)</div>
                      <input
                        data-testid="hardship-country"
                        type="text" value={form.hardship_country} onChange={update("hardship_country")}
                        maxLength={120}
                        placeholder="e.g. US"
                        className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                      />
                    </label>
                  </div>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Brief context (optional · helps our brief review)</div>
                    <textarea
                      data-testid="hardship-context"
                      value={form.hardship_context} onChange={update("hardship_context")}
                      maxLength={4000} rows={3}
                      placeholder="One or two sentences · we do not require proof beyond self-attestation."
                      className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
                    />
                  </label>
                </div>
              )}
            </div>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-1">Anything else the reader should know (optional)</div>
              <textarea
                data-testid="legal-advice-contact-note"
                value={form.contact_note} onChange={update("contact_note")}
                maxLength={2000} rows={2}
                className="w-full border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
              />
            </label>
            <button
              data-testid="legal-advice-submit"
              onClick={submit} disabled={busy}
              className="border-2 border-sage-accent bg-sage-accent text-sage-bg px-6 py-3 text-xs uppercase tracking-widest font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 transition-colors"
            >
              {busy ? "Filing…" : "File the matter"}
            </button>
          </div>
        )}

        <div className="mt-16 text-xs text-sage-faint sage-prose">
          Sage Legal Advice is not yet a licensed law firm in most US states. Until Door 1 (sandbox states) and Door 2 (attorney-on-retainer) land, filings queue and receive information-tier drafts. When your state's door opens, your matter is picked up first. Emergencies: dial the local bar's lawyer referral service or, if criminal and in custody, request court-appointed counsel.
        </div>
      </div>
    </div>
  );
}
